<template>
  <div :class="rootClasses" v-bind="$attrs">
    <WarRoomCommentCreateFormEditor
      ref="editor"
      class="war-room-comment-create-form__input"
      :autofocus="autofocus"
      :boost-usernames="boostUsernames"
      :initial-text="initialText"
      :image-key="imageKey"
      @post-shortcut-entered="handlePostComment"
      @text-change="handleTextChange"
      @is-anonymous-change="handleIsAnonymousChange"
      @image-change="handleImageChange"
      @image-remove="handleImageRemove"
      @image-uploading="handleImageUploading"
    >
      <template #action>
        <BravadoButtonV3
          v-if="!nested"
          size="sm"
          :disabled="isButtonDisabled"
          variant="accent"
          data-test="comment-save-button"
          @click="handlePostComment"
        >
          <SendIconSvg class="war-room-comment-create-form__post-button-icon" />
          <span class="war-room-comment-create-form__post-button-span">Comment</span>
        </BravadoButtonV3>
      </template>
      <template v-if="nested" #toolbar>
        <BravadoButtonV3
          :disabled="isButtonDisabled"
          variant="accent"
          size="sm"
          class="war-room-comment-create-form__post-button"
          data-test="comment-reply-button"
          @click="handlePostComment"
        >
          Reply
        </BravadoButtonV3>
        <BravadoButtonV3 variant="border" size="sm" @click="cancelComment">Cancel</BravadoButtonV3>
      </template>
    </WarRoomCommentCreateFormEditor>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from '@nuxtjs/composition-api';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import WarRoomCommentCreateFormEditor from '@/components/partials/war-room/WarRoomCommentCreateForm/WarRoomCommentCreateFormEditor.vue';
import SendIconSvg from '@/components/partials/war-room/WarRoomCommentCreateForm/images/send-comment.vue.svg';
import { dropRedundantWhitespace } from '@/components/partials/war-room/utils';

type WarRoomCommentCreateFormProps = {
  autofocus?: boolean;
  nested?: boolean;
  upserting?: boolean;
  initialText?: string;
  boostUsernames?: string[];
};
type WarRoomCommentCreateFormEmits = {
  (event: 'mounted' | 'cancel'): void;
  (event: 'post-comment', payload: { text: string; media: string | null; is_anonymous: boolean | null }): void;
};

const props = withDefaults(defineProps<WarRoomCommentCreateFormProps>(), {
  autofocus: false,
  nested: false,
  upserting: false,
  initialText: '',
  boostUsernames: () => [],
});
const emit = defineEmits<WarRoomCommentCreateFormEmits>();

onMounted(() => {
  emit('mounted');
});

const text = ref('');
const is_anonymous = ref<boolean | null>(null);
const imageKey = ref<string | null>(null);
const imageMedia = ref<string | null>(null);
const imageUploading = ref(false);
const profileLoaded = ref(false);
const editor = ref<HTMLElement | null>(null);

const isTextEmpty = computed(() => !text.value.replace(/&nbsp;|<br>/g, '').trim().length);
const rootClasses = computed(() => ({
  'war-room-comment-create-form': true,
  'war-room-comment-create-form--nested': props.nested,
}));
const isButtonDisabled = computed(
  () => isTextEmpty.value || props.upserting || imageUploading.value || !profileLoaded.value,
);

function handlePostComment() {
  if (isTextEmpty.value || imageUploading.value) {
    return;
  }
  emit('post-comment', {
    text: dropRedundantWhitespace(text.value),
    media: imageMedia.value,
    is_anonymous: is_anonymous.value,
  });
  clearEditor();
}

function handleTextChange(t: string) {
  text.value = t;
}

function handleIsAnonymousChange({ isAnonymous, loaded }: { isAnonymous: boolean; loaded: boolean }) {
  is_anonymous.value = isAnonymous;
  profileLoaded.value = loaded;
}

function handleImageChange({ key, media }: { key: string | null; media: string | null }) {
  imageKey.value = key;
  imageMedia.value = media;
}

function handleImageRemove() {
  handleImageChange({ key: null, media: null });
}

function handleImageUploading(state: boolean) {
  imageUploading.value = state;
}

function cancelComment() {
  emit('cancel');
}

// the following functions are exposed to parent components and are not used here
/* eslint-disable */
function focusEditor() {
  // @ts-expect-error
  editor.value?.focusInput();
}

function clearEditor() {
  // @ts-expect-error
  editor.value?.clearEditor();
}

function setText(text: string) {
  // @ts-expect-error
  editor.value?.setHTML(text);
}

/* eslint-enable */
defineExpose({
  focusEditor,
  clearEditor,
  setText,
});
</script>

<style scoped lang="scss">
.war-room-comment-create-form {
  $this: &;

  display: flex;
  justify-content: space-between;
  position: relative;
  align-items: flex-start;

  &--nested {
    margin-right: 0;
    display: initial;

    #{$this}__input {
      margin: 0 0 16px 0;
      min-height: 88px !important;
    }
  }

  &__input {
    width: 100%;
    min-height: 36px;

    @include mq('sm') {
      min-height: 40px;
    }
  }

  &__post-button {
    margin-right: 8px;
  }

  &__post-button-icon {
    display: inline;

    @include mq('sm') {
      display: none;
    }
  }

  &__post-button-span {
    display: none;

    @include mq('sm') {
      display: inline;
    }
  }
}
</style>
